"use client";
import React, { useState, useEffect } from "react";
import {
  Check,
  Users,
  Code,
  TrendingUp,
  Shield,
  Zap,
  Globe,
  Clock,
  TerminalSquare,
} from "lucide-react";
import { DotLottieReact, DotLottie } from "@lottiefiles/dotlottie-react";
import { useTheme } from "@/contexts/ThemeContext";

import { ThemeToggle } from "@/components/ThemeToggle";

import { siGithub, siX } from "simple-icons";
import { Logo } from "@/components/Logo";
import { Header } from "@/components/Header";
import { SimpleIcon } from "simple-icons";

// Helper to render Simple Icons as SVG using currentColor
const SimpleBrandIcon = ({
  icon,
  className,
  label,
}: {
  icon: { path: string };
  className?: string;
  label?: string;
}) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    aria-label={label}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d={(icon as SimpleIcon).path} fill="currentColor" />
  </svg>
);

export default function Index() {
  const [activeTab, setActiveTab] = useState(0);
  const [showFixed, setShowFixed] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const { resolvedTheme } = useTheme();
  const [isClient, setIsClient] = useState(false);
  const [currentAnimationData, setCurrentAnimationData] =
    useState<ArrayBuffer | null>(null);
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(false);
  const [animationError, setAnimationError] = useState(false);
  const [dotLottieRef, setDotLottieRef] = useState<DotLottie | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Simple animation cache - now with mobile/desktop separation
  const animationCache = React.useRef<{
    light?: ArrayBuffer;
    dark?: ArrayBuffer;
    lightMobile?: ArrayBuffer;
    darkMobile?: ArrayBuffer;
  }>({});

  // Ensure we're on the client side before rendering theme-dependent content
  useEffect(() => {
    setIsClient(true);
    
    // Detect if mobile device based on screen width
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    
    // Listen for resize to update mobile state
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simple animation loading
  useEffect(() => {
    if (!isClient) return;

    const loadAnimation = async () => {
      const theme = resolvedTheme === "dark" ? "dark" : "light";
      
      // Determine cache key based on device type
      const cacheKey = isMobile 
        ? (theme === "dark" ? "darkMobile" : "lightMobile")
        : theme;

      // Check cache first
      const cachedData = animationCache.current[cacheKey as keyof typeof animationCache.current];
      if (cachedData) {
        setCurrentAnimationData(cachedData);
        return;
      }

      setIsLoadingAnimation(true);
      setAnimationError(false);

      try {
        // Choose animation file based on device type
        let animationSrc: string;
        if (isMobile) {
          animationSrc = theme === "dark"
            ? "/animation-dark-mobile.lottie"
            : "/animation-light-mobile.lottie";
        } else {
          animationSrc = theme === "dark"
            ? "/animation-dark.lottie"
            : "/animation-light.lottie";
        }

        const response = await fetch(animationSrc);
        if (!response.ok)
          throw new Error(`Failed to fetch: ${response.status}`);

        const arrayBuffer = await response.arrayBuffer();

        // Store in cache with appropriate key
        animationCache.current[cacheKey as keyof typeof animationCache.current] = arrayBuffer;
        setCurrentAnimationData(arrayBuffer);
      } catch (error) {
        setAnimationError(true);
      } finally {
        setIsLoadingAnimation(false);
      }
    };

    loadAnimation();
  }, [isClient, resolvedTheme, isMobile]);

  // Aggressive memory management using official DotLottie methods
  useEffect(() => {
    if (dotLottieRef) {
      // Freeze animation when not visible to save memory
      const handleVisibilityChange = () => {
        if (document.hidden) {
          dotLottieRef.freeze(); // Freeze rendering to save resources
        } else {
          dotLottieRef.unfreeze(); // Resume rendering
        }
      };

      // Aggressive memory cleanup on scroll/resize
      const handleMemoryCleanup = () => {
        if (dotLottieRef && typeof dotLottieRef.freeze === "function") {
          dotLottieRef.freeze();
          setTimeout(() => {
            if (dotLottieRef && typeof dotLottieRef.unfreeze === "function") {
              dotLottieRef.unfreeze();
            }
          }, 100);
        }
      };

      // Throttle frame updates for better performance
      const throttledFrameHandler = () => {
        // Frame handling for performance optimization
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("scroll", handleMemoryCleanup, { passive: true });
      window.addEventListener("resize", handleMemoryCleanup, { passive: true });
      dotLottieRef.addEventListener("frame", throttledFrameHandler);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.removeEventListener("scroll", handleMemoryCleanup);
        window.removeEventListener("resize", handleMemoryCleanup);
        if (dotLottieRef.removeEventListener) {
          dotLottieRef.removeEventListener("frame", throttledFrameHandler);
        }
      };
    }
  }, [dotLottieRef]);

  // Simple cleanup
  useEffect(() => {
    return () => {
      if (dotLottieRef && typeof dotLottieRef.destroy === "function") {
        dotLottieRef.destroy();
      }
    };
  }, [dotLottieRef]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY <= 20) {
        // At very top (with small buffer) → show only static
        setShowFixed(false);
      } else if (currentY < lastScrollY && currentY > 50) {
        // Scrolling up and past threshold → show fixed overlay
        setShowFixed(true);
      } else if (currentY > lastScrollY && currentY > 100) {
        // Scrolling down and past threshold → hide fixed overlay
        setShowFixed(false);
      }

      setLastScrollY(currentY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const tabs = [
    {
      title: "For Marketers & Product Managers",
      icon: Users,
      headline: "Launch and Test in Real-Time",
      description:
        "Take direct control of the notification lifecycle with an intuitive, no-code CMS. Create, schedule, and manage all your in-app banners, alerts, and announcements without writing a single line of code or filing a single ticket.",
      benefits: [
        "Faster time-to-market for campaigns",
        "Quickly iterate on messaging based on performance",
        "Ensure brand consistency across all platforms",
      ],
    },
    {
      title: "For Developers",
      icon: TerminalSquare,
      headline: "Integrate Once, Then Focus on What Matters",
      description:
        "Reclaim your time from endless content update requests. Korrero offers a clean, well-documented API and a simple JavaScript SDK designed for a seamless developer experience (DX).",
      benefits: [
        "Reduce the burden of frequent, minor content updates",
        "Empower non-technical colleagues safely",
        "Integrate a scalable, reliable notification system in minutes",
      ],
    },
    {
      title: "For Business Leaders",
      icon: TrendingUp,
      headline: "Drive Agility and Efficiency",
      description:
        "Empower your teams to execute faster and optimize your digital communication strategy.",
      benefits: [
        "Improve team productivity by removing bottlenecks",
        "React instantly to market opportunities",
        "Optimize developer resources on high-value work",
      ],
    },
  ];

  return (
    <div className="min-h-screen text-theme-primary gradient-container transition-colors duration-150 ease-in-out">
      <div className="bg-main"></div>

      {/* Dual Header System */}
      <Header showFixed={showFixed} />

      {/* Hero Section */}
      <section className="px-4 sm:px-6 md:px-9 pt-12 md:pt-16 pb-8 md:pb-12 text-center">
        <div className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-3 mb-6 md:mb-8 border border-theme rounded-full bg-primary-10 backdrop-blur-md">
          <span className="text-sm sm:text-base text-theme-secondary">
            The Headless CMS for In-App Notifications
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-medium leading-tight md:leading-none mb-6 md:mb-8 bg-gradient-to-r from-[var(--color-text-primary)] from-70% to-transparent bg-clip-text text-transparent max-w-5xl mx-auto">
          In-App Notifications, Without the Headaches.
        </h1>

        <p className="text-base sm:text-lg text-korrero-gray-light max-w-3xl mx-auto mb-10 md:mb-16 leading-relaxed opacity-70">
          Empower your marketing and product teams to instantly manage all
          dynamic in-app content, while freeing your developers to focus on the
          core product. <em>Ship campaigns in minutes, not sprints.</em>
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-3 px-4 sm:px-0">
          <a
            href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=hero_action_button"
            className="px-6 sm:px-8 py-3 sm:py-4 border border-primary text-theme-primary rounded-full hover:bg-primary hover:text-white hover:border-white/20 transition-colors duration-300 ease-in-out text-base sm:text-lg font-medium w-full sm:w-auto"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Started Free
          </a>
        </div>

        <p className="text-sm text-theme-secondary opacity-60 max-w-2xl mx-auto leading-relaxed">
          No credit card required.
        </p>
      </section>

      {/* Dashboard Preview */}
      <section className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24">
        <div className="max-w-[calc(100vw-2rem)] mx-auto">
          <div className="relative">
            <div className="absolute -inset-10 bg-gradient-to-tr from-[var(--color-primary-dark)]/20 via-[var(--color-primary-main)]/20 to-[var(--color-primary-light)]/20 blur-3xl"></div>
            <div className="relative animation-height-responsive flex items-center justify-center">
              {isClient &&
              currentAnimationData &&
              !isLoadingAnimation &&
              !animationError ? (
                <DotLottieReact
                  dotLottieRefCallback={(dotLottie) => {
                    setDotLottieRef(dotLottie);
                  }}
                  data={currentAnimationData!}
                  loop
                  autoplay
                  speed={1}
                  style={{
                    width: "100%",
                    height: "100%",
                    maxHeight: isMobile ? "300px" : "100%",
                    objectFit: "contain",
                  }}
                  renderConfig={{
                    devicePixelRatio: 1.5,
                    autoResize: true,
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg opacity-60 animate-pulse">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-primary/20 rounded-full flex items-center justify-center">
                      <Zap className="w-8 h-8 text-primary" />
                    </div>
                    <p className="text-sm text-theme-secondary">
                      {animationError
                        ? "Dashboard preview unavailable"
                        : isLoadingAnimation
                        ? "Preparing your dashboard..."
                        : "Loading dashboard preview..."}
                    </p>
                    {animationError && (
                      <button
                        onClick={() => {
                          setAnimationError(false);
                          // Trigger reload by clearing cache
                          animationCache.current = {};
                        }}
                        className="mt-2 px-3 py-1 text-xs bg-primary/20 rounded-full hover:bg-primary/30 transition-colors"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - The Development Bottleneck */}
      <section
        id="how-it-works"
        className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24"
      >
        <div className="text-center mb-6 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium mb-4 sm:mb-6 max-w-3xl mx-auto leading-tight">
            Tired of Wasting Engineering Time on Content Updates?
          </h2>
          <p className="text-base sm:text-lg text-theme-secondary max-w-3xl mx-auto">
            Modern apps need timely, relevant communication, but managing in-app
            notifications is often a slow, inefficient process.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {[
            {
              icon: Code,
              title: "Developer Dependency",
              description:
                "Marketing and product teams are forced to wait for developers to make even minor text or image changes, creating bottlenecks that delay campaigns.",
              glow: "shadow-korrero-purple-glow-tertiary",
            },
            {
              icon: Users,
              title: "Inconsistent Messaging",
              description:
                "Without a central hub, managing notifications across web, mobile, and other platforms leads to an inconsistent brand voice and user experience.",
              glow: "shadow-korrero-purple-glow-primary",
            },
            {
              icon: Zap,
              title: "Lost Opportunities",
              description:
                "The inability to launch or modify promotional banners and alerts in response to market changes means you're moving too slow to capture revenue.",
              glow: "shadow-korrero-purple-glow-secondary",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-8 text-center"
            >
              <div
                className={`w-12 h-12 mx-auto mb-6 ${item.glow} shadow-2xl rounded-lg flex items-center justify-center`}
              >
                <item.icon className="w-6 h-6 text-theme-primary" />
              </div>
              <h3 className="text-xl font-medium mb-4">{item.title}</h3>
              <p className="text-sm text-theme-secondary">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* "FOR YOUR TEAM" SECTION - A Solution for Everyone */}
      <section
        id="for-your-team"
        className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24"
      >
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-medium mb-6 max-w-3xl mx-auto leading-tight">
            The Command Center for Cross-Functional Teams.
          </h2>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            Korrero bridges the gap between technical and non-technical teams,
            providing the right tools for every role.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 overflow-x-auto px-4 sm:px-0">
          <div className="bg-[var(--color-bg-paper)]/5 border border-theme rounded-3xl sm:rounded-4xl p-1 sm:whitespace-nowrap flex sm:flex-row flex-col sm:gap-0 gap-1">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-sm font-medium transition-all cursor-pointer ${
                  activeTab === index
                    ? "bg-accent text-[var(--color-text-inverse-default)]"
                    : "text-korrero-gray-light hover:text-theme-primary hover:bg-[var(--color-bg-paper)]/10"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-6 sm:p-8 md:p-12 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center mb-4 gap-3">
              <div className="w-16 h-8 bg-gradient-to-tr from-korrero-purple-secondary to-korrero-purple-primary rounded-2xl flex items-center justify-center">
                {(() => {
                  const IconComponent = tabs[activeTab].icon;
                  return <IconComponent className="w-8 h-8 text-primary" />;
                })()}
              </div>

              <h3 className="text-xl sm:text-2xl md:text-3xl font-medium leading-tight">
                {tabs[activeTab].headline}
              </h3>
            </div>

            <div className="min-h-[6rem] sm:min-h-[6rem] mb-6 sm:mb-8 flex items-start justify-center">
              <p className="text-sm sm:text-base md:text-lg text-theme-secondary leading-relaxed max-w-2xl mx-auto">
                {tabs[activeTab].description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {tabs[activeTab].benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center text-sm text-left sm:text-center"
                >
                  <div className="w-6 h-6 bg-gradient-to-tr from-[var(--color-primary-dark)] to-[var(--color-primary-some-light)] rounded-full box-shadow-white border-theme flex items-center justify-center mr-2 flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CORE FEATURES SECTION (MVP FOCUS) */}
      <section id="features" className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-medium mb-6 max-w-3xl mx-auto leading-tight">
            Everything You Need to Get Started.
          </h2>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            Manage the complete notification lifecycle from a single, flexible
            platform, giving your team maximum speed and control.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto mb-10 md:mb-16">
          {[
            {
              icon: Users,
              title: "Intuitive Content Management",
              subtitle: "A simple, powerful CMS for your non-technical team.",
              features: [
                "Create and manage content with support for Markdown, image URLs, and actions",
                "Activate and deactivate notifications with a single click",
                "Manage multiple websites or apps under separate 'Projects'",
              ],
              glow: "shadow-korrero-purple-glow-primary",
            },
            {
              icon: Clock,
              title: "Simple Scheduling & Display Rules",
              subtitle: "Deliver the right message at the right time.",
              features: [
                "Set starts_at and ends_at timestamps to control notification visibility",
                "Use predefined display presets like 'top_banner' or 'modal' to control appearance without code",
              ],
              glow: "shadow-korrero-purple-glow-secondary",
            },
            {
              icon: Code,
              title: "Flexible Developer Tools",
              subtitle: "A developer experience you'll actually love.",
              features: [
                "A secure, project-specific REST API to fetch active notifications",
                "A lightweight JavaScript SDK that can auto-render notifications or fetch data for custom UIs",
                "Simple endpoints for tracking impressions and clicks",
              ],
              glow: "shadow-korrero-purple-glow-tertiary",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-8"
            >
              <div
                className={`w-12 h-12 mb-6 ${item.glow} shadow-2xl rounded-lg flex items-center justify-center`}
              >
                <item.icon className="w-6 h-6 text-theme-primary" />
              </div>
              <h3 className="text-2xl font-medium mb-3 leading-tight">
                {item.title}
              </h3>
              <p className="text-sm text-theme-secondary mb-6 leading-relaxed">
                {item.subtitle}
              </p>
              <ul className="space-y-3">
                {item.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start text-sm">
                    <div className="w-2 h-2 bg-korrero-purple-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Future Roadmap Teaser */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-8">
            <h3 className="text-2xl font-medium mb-4">Coming Soon</h3>
            <p className="text-theme-secondary">
              We&#39;re building on this foundation with A/B testing, advanced
              analytics, localization support, and deeper integrations.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST & RELIABILITY SECTION */}
      <section className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-medium mb-4 max-w-3xl mx-auto leading-tight">
            Built for Trust, Security, and Scale.
          </h2>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            We understand the challenges of integrating new technology, so
            we&#39;ve built our platform on a foundation of reliability and best
            practices.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {[
            {
              icon: Shield,
              title: "Security & Compliance",
              description:
                "Managing user data requires rigorous compliance. We are fully GDPR compliant and prioritize data privacy in every feature we build.",
              glow: "shadow-korrero-purple-glow-secondary",
            },
            {
              icon: Code,
              title: "Seamless Integration",
              description:
                "Worried about complexity? Our API-first approach and clear documentation are designed to prevent integration hurdles and ensure developers can get up and running quickly.",
              glow: "shadow-korrero-purple-glow-tertiary",
            },
            {
              icon: Globe,
              title: "Scalable Architecture",
              description:
                "Our cloud-based infrastructure is designed to be reliable and scale with your business, from your first user to millions.",
              glow: "shadow-korrero-purple-glow-primary",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-8 text-center"
            >
              <div
                className={`w-12 h-12 mx-auto mb-6 ${item.glow} shadow-2xl rounded-lg flex items-center justify-center`}
              >
                <item.icon className="w-6 h-6 text-theme-primary" />
              </div>
              <h3 className="text-xl font-medium mb-4">{item.title}</h3>
              <p className="text-sm text-theme-secondary">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-medium mb-4 max-w-3xl mx-auto leading-tight">
            Simple, transparent pricing.
          </h2>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            Choose Monthly or Yearly billing. Upgrade anytime.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-[var(--color-bg-paper)]/5 border border-theme rounded-full p-1 inline-flex">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                billingCycle === "monthly"
                  ? "bg-accent text-[var(--color-text-inverse-default)]"
                  : "text-korrero-gray-light hover:text-theme-primary hover:bg-[var(--color-bg-paper)]/10"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                billingCycle === "yearly"
                  ? "bg-accent text-[var(--color-text-inverse-default)]"
                  : "text-korrero-gray-light hover:text-theme-primary hover:bg-[var(--color-bg-paper)]/10"
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-6 md:gap-8 max-w-6xl mx-auto">
          {[
            {
              key: "FREE" as const,
              name: "Free",
              priceMonthly: 0.0,
              priceYearly: 0.0,
              description: "Everything you need to get started.",
              features: [
                "Only 1 project",
                "5 notifications per month",
                "1 team members",
                "1 API key per project",
                "Basic analytics",
                "Email support",
              ],
              cta: {
                label: "Get Started Free",
                href: "https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=get_started_free_button",
              },
              highlight: false,
            },
            {
              key: "PRO" as const,
              name: "Pro",
              priceMonthly: 19.99,
              priceYearly: 199.99,
              description: "Best for growing teams and projects.",
              features: [
                "Up to 3 projects",
                "Unlimited notifications",
                "Up to 5 team members per project",
                "Unlimited API keys",
                "Custom presets",
                "Enhanced analytics",
                "Email support",
              ],
              cta: {
                label: "Start Pro",
                href: "https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=pro_button",
              },
              highlight: true,
            },
            {
              key: "ENTERPRISE" as const,
              name: "Enterprise",
              priceMonthly: 99.99,
              priceYearly: 999.99,
              description: "Advanced needs, custom limits, priority support.",
              features: [
                "Unlimited projects",
                "Unlimited notifications",
                "Unlimited team members",
                "Unlimited API keys",
                "Custom presets",
                "Enhanced analytics",
                "Priority email support",
                "Advanced integrations",
              ],
              cta: {
                label: "Start Enterprise",
                href: "https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=enterprise_button",
              },
              highlight: false,
            },
          ].map((tier, idx) => {
            const amount =
              billingCycle === "monthly" ? tier.priceMonthly : tier.priceYearly;
            const cadenceLabel = billingCycle === "monthly" ? "mo" : "yr";
            const ctaLabel = tier.cta.label;
            const ctaHref = `https://console.korrero.com/register?plan=${tier.key.toLowerCase()}&cycle=${billingCycle}&utm_source=landing&utm_medium=landing&utm_campaign=${tier.key.toLowerCase()}_button`;
            return (
              <div
                key={idx}
                className={`bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-8 h-full flex flex-col ${
                  tier.highlight ? "ring-1 ring-theme/20" : ""
                }`}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-2xl font-medium leading-tight">
                    {tier.name}
                  </h3>
                  {tier.highlight && (
                    <span className="text-xs px-2 py-1 rounded-full bg-primary-10 border border-primary-10">
                      Popular
                    </span>
                  )}
                </div>
                <p
                  className={`text-sm text-theme-secondary leading-relaxed ${
                    tier.key === "ENTERPRISE" ? "line-clamp-2 mb-2" : "mb-8"
                  }`}
                >
                  {tier.description}
                </p>

                <div className="mb-6">
                  {amount === null ? (
                    <span className="text-xl">Contact us</span>
                  ) : (
                    <div className="flex items-end justify-start gap-2">
                      <span className="text-5xl font-semibold">${amount}</span>
                      <span className="text-theme-secondary mb-1">
                        / {cadenceLabel}
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-sm">
                      <div className="w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-theme-primary" />
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={ctaHref}
                  className={`mt-auto inline-block w-full text-center px-6 py-3 rounded-full font-medium transition-colors duration-300 ease-in-out ${
                    tier.highlight
                      ? "bg-accent text-[var(--color-text-inverse-default)] hover:bg-primary hover:text-white"
                      : "border border-theme text-theme-primary hover:bg-primary hover:text-white"
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ctaLabel}
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CALL-TO-ACTION (CTA) SECTION */}
      <section className="px-4 sm:px-6 md:px-9 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-tr from-[var(--color-primary-main)]/0 to-[var(--color-primary-main)]/2 backdrop-blur-sm border border-theme rounded-3xl p-6 sm:p-10 md:p-16 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight">
              Ready to Streamline Your In-App Communications?
            </h2>
            <p className="text-base sm:text-lg text-theme-secondary mb-6 sm:mb-8 md:mb-12 max-w-3xl mx-auto">
              Stop the back-and-forth. Empower your team, move faster, and
              deliver a better user experience.
            </p>
            <a
              href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=final_cta_button"
              className="px-6 sm:px-8 md:px-12 py-3 sm:py-4 bg-accent text-[var(--color-text-inverse-default)] rounded-full hover:bg-primary hover:text-white hover:border-theme transition-colors duration-300 ease-in-out font-medium text-base sm:text-lg w-full sm:w-auto inline-block"
            >
              Sign Up and Launch Your First Notification for Free
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-6 md:px-9 pt-12 md:pt-16 pb-8 border-t border-theme">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-1">
              <div className="flex items-center mb-6">
                <Logo className="h-8 mr-3 text-primary" />
              </div>
              <p className="text-theme-secondary mb-10 text-sm leading-relaxed">
                Empowering product teams to deliver exceptional in-app
                notification experiences.
              </p>
              <div>
                <p className="font-medium mb-2">Follow us</p>
                <div className="flex space-x-3">
                  <div className="w-10 h-10 bg-theme-card border border-white/20 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors duration-300 ease-in-out cursor-pointer">
                    {/* X (Twitter) brand icon via simple-icons */}
                    <a
                      href="https://x.com/korrerocms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SimpleBrandIcon
                        icon={siX}
                        className="w-5 h-5"
                        label="X"
                      />
                    </a>
                  </div>
                  <div className="w-10 h-10 bg-theme-card border border-white/20 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors duration-300 ease-in-out cursor-pointer">
                    <a
                      href="https://github.com/korreroltd"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SimpleBrandIcon
                        icon={siGithub}
                        className="w-5 h-5"
                        label="GitHub"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="text-lg font-medium mb-6">Product</h4>
              <ul className="space-y-3 text-theme-secondary">
                <li>
                  <a
                    href="#features"
                    className="hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="https://docs.korrero.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-theme-primary hover:text-shadow-white transition-colors group relative inline-flex items-center"
                  >
                    <span>Docs</span>
                    <span className="absolute -top-1 -right-3 text-[10px] opacity-70 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                      ↗
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-lg font-medium mb-6">Company</h4>
              <ul className="space-y-3 text-theme-secondary">
                <li>
                  <a
                    href="mailto:support@korrero.com"
                    className="hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="text-lg font-medium mb-6">Legal</h4>
              <ul className="space-y-3 text-theme-secondary">
                <li>
                  <a
                    href="/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    <span>Privacy Policy</span>
                    <span className="absolute -top-1 -right-3 text-[10px] opacity-70 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                      ↗
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="/legal/terms-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    <span>Terms & Conditions</span>
                    <span className="absolute -top-1 -right-3 text-[10px] opacity-70 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                      ↗
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="/legal/cookie-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    <span>Cookie Policy</span>
                    <span className="absolute -top-1 -right-3 text-[10px] opacity-70 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                      ↗
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="/legal/data-processing-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center hover:text-theme-primary hover:text-shadow-white transition-colors"
                  >
                    <span className="hidden sm:inline">
                      Data Processing Agreement
                    </span>
                    <span className="inline sm:hidden">DPA</span>
                    <span className="absolute -top-1 -right-3 text-[10px] opacity-70 transition-opacity group-hover:opacity-100 group-hover:text-primary">
                      ↗
                    </span>
                  </a>
                </li>
              </ul>
              <div className="mt-4 flex justify-end hidden sm:block">
                <ThemeToggle />
              </div>
            </div>
          </div>

          <div className="mb-4 flex justify-center sm:hidden">
            <ThemeToggle />
          </div>

          <div className="border-t border-theme pt-8">
            <p className="text-center text-theme-secondary text-sm">
              © 2025 Korrero. All rights reserved. Made in{" "}
              <a
                href="https://ukraine.ua"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center hover:text-primary transition-colors"
              >
                <span>🇺🇦 Ukraine</span>
                <span className="absolute -top-1 -right-2 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
                  ↗
                </span>
              </a>
              .
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
